import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ComplimentaryCareEnrollment, EnrollmentStatus } from '../../api/types';
import { useHubContext } from '../../shell/HubContext';
import { EnrollmentTable, type SortKey } from './EnrollmentTable';
import { ReviewConfirmModal } from './ReviewActions';
import { useReviewActions } from './useReviewActions';

type StatusFilter = EnrollmentStatus | 'all';

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'approved', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

/**
 * Whether an enrollment matches what was typed.
 *
 * Every whitespace-separated word has to appear somewhere, rather than the whole string having to
 * appear in one field: "doe jamie" and "jamie doe" both find the same person, and "moreau oak" finds
 * the Moreau enrollment at the Oak Park clinic. Matching the raw string against each field in turn —
 * the obvious version — fails both of those.
 */
function matchesSearch(enrollment: ComplimentaryCareEnrollment, terms: string[]): boolean {
  if (terms.length === 0) {
    return true;
  }

  // HR's own note is in here too: whatever they wrote to find this person again later — a clinic
  // they are waiting on, a case number — only works as a handle if searching for it finds the row.
  const haystack = [
    enrollment.employeeDisplayName,
    enrollment.employeeUpn,
    enrollment.hrNote ?? '',
    ...enrollment.enrollees.flatMap((e) => [e.name, e.wellnessCenter]),
  ]
    .join(' ')
    .toLowerCase();

  return terms.every((term) => haystack.includes(term));
}

/**
 * The HR dashboard for the "Employee Complimentary Chiropractic Care Enrollment" form. Pending
 * submissions get their own queue at the top — they are the work; everything else is the record of
 * work already done. A row opens the enrollment's own page.
 */
export function ComplimentaryCareReviewSection() {
  const { api } = useHubContext();
  const navigate = useNavigate();

  const [enrollments, setEnrollments] = useState<ComplimentaryCareEnrollment[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  // Active is what HR is normally looking at: who currently has care. Deactivated and rejected rows
  // are the record of decisions already made, one tab away, and the count below the table says how
  // many are being held back so the default never looks like data that went missing.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('approved');
  const [sortKey, setSortKey] = useState<SortKey>('submittedAtUtc');
  const [sortDescending, setSortDescending] = useState(true);

  const actions = useReviewActions(api, async () =>
    setEnrollments(await api.listComplimentaryCareEnrollments()),
  );

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        setEnrollments(await api.listComplimentaryCareEnrollments(controller.signal));
      } catch (err) {
        if (!controller.signal.aborted) {
          setLoadError(err instanceof Error ? err.message : 'Could not load enrollments.');
        }
      }
    })();

    return () => controller.abort();
  }, [api]);

  const { pending, reviewed, hiddenByStatus } = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);

    const sort = (rows: ComplimentaryCareEnrollment[]) =>
      [...rows].sort((a, b) => {
        const result = String(a[sortKey]).localeCompare(String(b[sortKey]));
        return sortDescending ? -result : result;
      });

    const found = (enrollments ?? []).filter((e) => matchesSearch(e, terms));
    const decided = found.filter((e) => e.status !== 'pendingReview');

    return {
      pending: sort(found.filter((e) => e.status === 'pendingReview')),
      reviewed: sort(decided.filter((e) => statusFilter === 'all' || e.status === statusFilter)),

      // A search that matches rows the status tab is hiding otherwise looks like a broken search.
      hiddenByStatus: statusFilter === 'all'
        ? 0
        : decided.filter((e) => e.status !== statusFilter).length,
    };
  }, [enrollments, search, statusFilter, sortKey, sortDescending]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDescending((d) => !d);
    } else {
      setSortKey(key);
      setSortDescending(false);
    }
  }

  if (loadError) {
    return (
      <div className="page-message page-message--error">
        <h2>Could not load enrollments</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  if (enrollments === null) {
    return <div className="page-message">Loading…</div>;
  }

  const open = (enrollment: ComplimentaryCareEnrollment) =>
    navigate(`/complimentary-care-review/${enrollment.id}`);

  return (
    <div className="care-page care-page--wide">
      <header className="care-page__header">
        <h1 className="care-page__title">Chiropractic Care Enrollments</h1>
        <p className="care-page__subtitle">
          Validate the form and its documents, then approve so the clinic can schedule the first
          appointments.
        </p>
      </header>

      <div className="care-review__toolbar">
        <input
          className="control care-review__search"
          type="search"
          placeholder="Search employee, enrollee, clinic…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search.trim().length > 0 && (
          <span className="care-review__count">
            {pending.length + reviewed.length} match{pending.length + reviewed.length === 1 ? '' : 'es'}
            <button type="button" className="care-document-link" onClick={() => setSearch('')}>
              clear
            </button>
          </span>
        )}
      </div>

      {actions.error && (
        <div className="issues__block issues__block--error">
          <p>{actions.error}</p>
        </div>
      )}

      <section className="care-review__group">
        <div className="care-review__group-header">
          <h2 className="care-review__group-title">
            Pending review
            {pending.length > 0 && <span className="care-count">{pending.length}</span>}
          </h2>
        </div>

        <EnrollmentTable
          enrollments={pending}
          emptyMessage="Nothing waiting for review."
          sortKey={sortKey}
          sortDescending={sortDescending}
          onSort={toggleSort}
          onOpen={open}
          onAction={actions.request}
          busyId={actions.busyId}
        />
      </section>

      <section className="care-review__group">
        <div className="care-review__group-header">
          <h2 className="care-review__group-title">Reviewed</h2>

          <div className="care-review__status-filter" role="group" aria-label="Filter by status">
            {statusFilters.map((option) => (
              <button
                key={option.value}
                type="button"
                className={
                  statusFilter === option.value
                    ? 'button button--quiet button--small is-selected'
                    : 'button button--quiet button--small'
                }
                aria-pressed={statusFilter === option.value}
                onClick={() => setStatusFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <EnrollmentTable
          enrollments={reviewed}
          emptyMessage="No enrollments match this filter."
          sortKey={sortKey}
          sortDescending={sortDescending}
          onSort={toggleSort}
          onOpen={open}
          onAction={actions.request}
          busyId={actions.busyId}
        />

        {hiddenByStatus > 0 && (
          <p className="care-review__hidden">
            {hiddenByStatus} more in other statuses —{' '}
            <button type="button" className="care-document-link" onClick={() => setStatusFilter('all')}>
              show all
            </button>
          </p>
        )}
      </section>

      <ReviewConfirmModal
        confirming={actions.confirming}
        note={actions.note}
        noteError={actions.noteError}
        busy={actions.busyId !== null}
        onNoteChange={actions.setNote}
        onConfirm={actions.confirm}
        onCancel={actions.cancel}
      />
    </div>
  );
}
